/**
 * CoPilot attachment upload — client wire contract, PRIORITY 1.
 *
 * `apps/citizen-mobile/core/lib/copilot-upload.ts` streams a picked file with
 * an `expo-file-system` `UploadTask` — entirely outside `@tonnie/sdk`, because
 * a device file cannot be uploaded through a JS Blob on React Native — and then
 * does:
 *
 *     const body = JSON.parse(res.body) as UploadResponseBody;
 *     return { attachmentId: body.attachmentId, …, text: body.transcript ?? body.extractedText ?? '' };
 *
 * A bare `as` cast. Nothing validates it. Rename `attachmentId` on the server
 * and the phone gets `undefined`, attaches it to the CoPilot turn, and
 * fails somewhere further on with no indication of the cause.
 *
 * Reached by three routes, all with the same response shape:
 *   POST /api/copilot/uploads/image
 *   POST /api/copilot/uploads/document
 *   POST /api/copilot/uploads/audio
 *   POST /api/copilot/uploads/video
 */
import { z } from 'zod';

/**
 * The four kinds the phone switches on. A NEW kind is safe — the client picks
 * the endpoint itself and only ever receives back the kind it asked for — so
 * this is a `string` narrowed by the four known values rather than a closed
 * enum that a fifth kind would break.
 */
export const clientWireAttachmentKindSchema = z.enum(['image', 'document', 'audio', 'video']);

export const clientWireCopilotUploadResponseSchema = z.object({
  /** Read positionally and passed into the turn. Renaming it is a silent break. */
  attachmentId: z.string().min(1),
  fileName: z.string(),
  mimeType: z.string(),
  /** A NUMBER. Serialising it as a string breaks the size display and any arithmetic. */
  fileSize: z.number(),
  kind: clientWireAttachmentKindSchema,

  /**
   * `transcript` (audio + video) and `extractedText` (image + document) are the
   * same slot under two names, and the phone coalesces them:
   *
   *     text: body.transcript ?? body.extractedText ?? ''
   *
   * So BOTH are optional here and neither may become required. What must not
   * happen is either arriving as `null` where the phone expects a string or
   * absence — `??` treats null as absent, so null is survivable, but a NUMBER
   * or an object in that slot is not. Typed as optional strings, which rejects
   * exactly that.
   */
  transcript: z.string().optional(),
  extractedText: z.string().optional(),
});

export type ClientWireCopilotUploadResponse = z.infer<typeof clientWireCopilotUploadResponseSchema>;
